import React, { useEffect, useRef, useState } from 'react'
import {
  Button,
  IconCaretLeft,
  Stack,
  csx,
  dataAttr,
  useModalState,
} from '@vtex/admin-ui'
import { useHistory, useLocation } from 'react-router'
import { FormattedMessage } from 'react-intl'
import { useDispatch } from 'react-redux'

import { PageModal } from '../../../Checkout/components/PageModal'
import { PageModalHeader } from '../../../Checkout/components/PageModal/PageModalHeader'
import { SearchInput } from '../SearchInput'
import {
  SearchRows,
  SearchRowsItem,
  SearchRowsRecentIcon,
  SearchRowsTitle,
} from '../SearchRows'
import { useSearch } from '../../hooks/useSearch'
import { useQueryParams } from '../../../../core/router'
import { useDevice } from '../../../../core/hooks/useDevice'
import { useBarcodeReader } from '../../hooks/useBarcodeReader'

export const SearchModalMobile: React.FC = () => {
  const location = useLocation()
  const history = useHistory()
  const query = useQueryParams()
  const dispatch = useDispatch()
  const { isNativeDevice } = useDevice()
  const { barcode, openBarcodeReader } = useBarcodeReader()

  const modal = useModalState({
    open: location.hash.includes('#/search-mobile-modal'),
  })

  const {
    search: { recentTerms },
    addRecentTerm,
  } = useSearch()

  const inputRef = useRef<HTMLInputElement>(null)

  const [term, setTerm] = useState('')

  useEffect(() => {
    if (modal.open) {
      inputRef.current?.focus()
    }
  }, [modal])

  useEffect(() => {
    setTerm(query.get('term') ?? '')
  }, [location, query])

  const handleChange = (value: string) => {
    setTerm(value)
  }

  const handleBack = () => {
    setTerm('')
    history.goBack()
  }

  const handleClear = () => {
    setTerm('')
    inputRef?.current?.focus()
  }

  const handleSubmit = (value?: string) => {
    if (!value && (!term || term.trim() === '')) return

    return history.replace({
      pathname: '/new-search',
      search: `term=${value ?? term.trim()}`,
      state: { forceUpdate: new Date().getTime() },
    })
  }

  const handleSubmitAndSaveRecentTerm = (value?: string) => {
    if (!value && (!term || term.trim() === '')) return

    dispatch(addRecentTerm({ term: value ?? term.trim(), productName: '' }))
    handleSubmit(value)
  }

  useEffect(() => {
    if (barcode) {
      setTerm(barcode)
      handleSubmitAndSaveRecentTerm(barcode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcode])

  const onSelectRecentSearchItem = (value: string) => {
    inputRef?.current?.focus()
    setTerm(value)
    handleSubmit(value)
  }

  const handleScanClick = (event: React.MouseEvent) => {
    event.preventDefault()
    openBarcodeReader()
  }

  return (
    <PageModal state={modal} data-testid="search-modal-mobile">
      <div
        className={csx({
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
        })}
      >
        <PageModalHeader>
          <Stack direction="row" space="$space-2">
            <Button
              data-testid="search-modal-back-button"
              variant="tertiary"
              icon={<IconCaretLeft color="black" />}
              onClick={handleBack}
              className={csx({
                [dataAttr('size', 'normal')]: {
                  padding: 0,
                },
              })}
            />
            <SearchInput
              ref={inputRef}
              value={term}
              onChange={(e) => handleChange(e.target.value)}
              onSubmit={() => handleSubmitAndSaveRecentTerm()}
              onClear={handleClear}
              className={csx({ flex: 1 })}
            />
          </Stack>
        </PageModalHeader>
      </div>
      <div
        className={csx({
          overflowY: 'auto',
          maxHeight: '40%',
          paddingX: '$space-5',
          paddingBottom: '$space-16',
        })}
      >
        <SearchRows>
          {recentTerms?.map(({ term: searchTerm }, index) => (
            <SearchRowsItem
              key={`RecentSearch-${index}`}
              onPress={() => onSelectRecentSearchItem(searchTerm)}
            >
              <SearchRowsTitle name={searchTerm} />
              <SearchRowsRecentIcon />
            </SearchRowsItem>
          ))}
        </SearchRows>
      </div>
      {isNativeDevice && (
        <div
          className={csx({
            position: 'fixed',
            backgroundColor: '$white',
            width: 'full',
            paddingTop: '$space-2',
          })}
        >
          <Button
            data-testid="search-modal-scan-button"
            variant="tertiary"
            onClick={handleScanClick}
            className={csx({
              border: '$neutral',
              borderRadius: '2rem',
              width: '90%',
              [dataAttr('size', 'normal')]: {
                marginX: '$space-5',
              },
            })}
          >
            <FormattedMessage id="search.scan-button" />
          </Button>
        </div>
      )}
    </PageModal>
  )
}
